import { trackDesignSystemAction, trackSceneAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import ProfileIcon from 'components/Panel/RightPanel/ProfileIcon';
import ProjectListItem from 'components/Sidebar/ListItem/ProjectListItem';
import { Loader } from 'components/Widget/Loader';
import Logo from 'components/Widget/Logo';
import { DesignSystem, Scene } from 'dragonmouse-shared/interfaces';
import { useSidebarWidth } from 'hooks/useSidebarWidth';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import {
	selectDesignSystems,
	selectGuestDesignSystems,
	selectGuestScenes,
	selectIsGuest,
	selectOrganizationDesignSystemIds,
	selectOrganizations,
	selectOrganizationSceneIds,
	selectScenes,
	selectUser,
	selectUserDesignSystemIds,
	selectUserSceneIds,
} from 'redux/db/reducers';
import { setSidebarDocked, setSidebarHovered, setSidebarVisible } from 'redux/mode/actions';
import { selectIsMobile, selectSettings } from 'redux/mode/reducers';
import { RootReducer, selectIsLoadingAnything } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import AccountButton from './AccountButton';
import DesignSystemListItem from './ListItem/DesignSystemListItem';
import NewButton from './NewButton';
import * as S from './styles';

const Sidebar = () => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const navigate = useNavigate();
	const sidebarWidth = useSidebarWidth();

	const {
		sidebarVisible: sidebarVisibleFromState,
		sidebarDocked,
		sidebarHovered,
		contextMenu,
		scenes,
		guestScenes,
		designSystems,
		guestDesignSystems,
		user,
		isMobile,
		activeSceneId,
		activeDesignSystemId,
		loading,
		isGuest,
		organizations,
		organizationSceneIds,
		organizationDesignSystemIds,
		userSceneIds,
		userDesignSystemIds,
	} = useSelector((r: RootReducer) => {
		return {
			sidebarVisible: r.mode.sidebarVisible,
			sidebarDocked: r.mode.sidebarDocked,
			sidebarHovered: r.mode.sidebarHovered,
			contextMenu: r.mode.contextMenu,
			scenes: selectScenes(r.db),
			guestScenes: selectGuestScenes(r.db),
			designSystems: selectDesignSystems(r.db),
			guestDesignSystems: selectGuestDesignSystems(r.db),
			user: selectUser(r.db),
			isMobile: selectIsMobile(r.mode),
			activeSceneId: r.mode.activeSceneId,
			activeDesignSystemId: r.mode.activeDesignSystemId,
			loading: selectIsLoadingAnything(r),
			createOrganizationEnabled: selectSettings(r.mode).createOrganizationEnabled,
			isGuest: selectIsGuest(r.db),
			organizations: selectOrganizations(r.db),
			organizationSceneIds: selectOrganizationSceneIds(r.db),
			organizationDesignSystemIds: selectOrganizationDesignSystemIds(r.db),
			userSceneIds: selectUserSceneIds(r.db),
			userDesignSystemIds: selectUserDesignSystemIds(r.db),
		};
	}, shallowEqual);

	// Compute sidebar visibility: Mobile uses sidebarVisible directly, Desktop uses docked || hovered
	const sidebarVisible = isMobile ? sidebarVisibleFromState : sidebarDocked || sidebarHovered;

	//----------------------------------------------------------------------------------------------
	// STATE MANAGEMENT
	//----------------------------------------------------------------------------------------------
	const [showAllDesignSystems, setShowAllDesignSystems] = useState(false);
	const [showAllGuestDesignSystems, setShowAllGuestDesignSystems] = useState(false);
	const [showAllProjects, setShowAllProjects] = useState(false);
	const [showAllGuestProjects, setShowAllGuestProjects] = useState(false);
	const [filterText, setFilterText] = useState('');

	// Reset expanded state when user selects an item
	useEffect(() => {
		setShowAllDesignSystems(false);
		setShowAllGuestDesignSystems(false);
		setShowAllProjects(false);
		setShowAllGuestProjects(false);
	}, [activeSceneId, activeDesignSystemId]);

	//----------------------------------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------------------------------

	const onLoadScene = (scene: Scene, newTab?: boolean) => {
		if (newTab) {
			trackSceneAction('open_in_new_tab');
			window.open(routes.scene(scene.id, scene.title), '_blank', 'noopener,noreferrer');
		} else {
			trackSceneAction('open');
			navigate(routes.scene(scene.id, scene.title));
			if (isMobile) {
				dispatch(setSidebarVisible(false));
			}
		}
	};

	const onLoadDesignSystem = (designSystem: DesignSystem, newTab?: boolean) => {
		if (newTab) {
			trackDesignSystemAction('open_in_new_tab');
			window.open(
				routes.designSystem(designSystem.id, designSystem.title),
				'_blank',
				'noopener,noreferrer'
			);
		} else {
			trackDesignSystemAction('open');
			navigate(routes.designSystem(designSystem.id, designSystem.title));
		}
	};

	const handleMouseEnter = () => {
		if (!isMobile && !sidebarDocked) {
			dispatch(setSidebarHovered(true));
		}
	};

	const handleMouseLeave = () => {
		// Don't close sidebar if context menu is open (user is right-clicking)
		//
		// This is part of our compromise solution to handle context menu interactions in the sidebar.
		// The fundamental challenge is that, when the mouse enters the context menu, it triggers a
		// mouseLeave event on the sidebar.
		//
		// TLDR we can't detect things cleanly without a major refactor of context menu.
		// So accepting compromise:
		//   - Block closing while a context menu is open.
		//   - When the menu closes (accept or cancel), the sidebar stays open until the user hovers away
		//   - Sometimes this means the user has to hover onto, and then off of the sidebar to close it.
		//
		// This is not ideal but, this is not a time to let perfect be the enemy of good.
		// This is not a frequent interaction after all...
		//
		// ALTERNATIVES CONSIDERED:
		// 1. Close sidebar immediately when menu closes - feels abrupt, especially when making a selection
		// 2. Detect hover state when menu closes (via refs, timers, :hover) - unreliable, complex
		// 3. Click-outside-to-close model - more predictable but changes the interaction paradigm
		//
		// TRADEOFF:
		// The sidebar may linger open after menu interaction until user hovers away.
		// This is acceptable because it's predictable and the code stays simple.
		if (!isMobile && !sidebarDocked && !contextMenu) {
			dispatch(setSidebarHovered(false));
		}
	};

	//----------------------------------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------------------------------
	const DESIGN_SYSTEMS_LIMIT = 5;
	const PROJECTS_LIMIT = 15;

	const userSceneIdSet = new Set(userSceneIds);
	const sortedScenes: Scene[] = [...scenes]
		.filter((s: Scene) => userSceneIdSet.has(s.id))
		.sort((a, b) => {
			const getMostRecentAction = (scene: Scene) => {
				const creationDate = new Date(scene.createdAt);
				if (!scene.metadata?.openActions || !user?.id) return creationDate;
				const userAction = scene.metadata.openActions[user.id];
				if (!userAction) return creationDate;
				return new Date(userAction);
			};
			return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
		});

	const sortedGuestScenes: Scene[] = [...guestScenes].sort((a, b) => {
		const getMostRecentAction = (scene: Scene) => {
			const creationDate = new Date(scene.createdAt);
			if (!scene.metadata?.openActions || !user?.id) return creationDate;
			const userAction = scene.metadata.openActions[user.id];
			if (!userAction) return creationDate;
			return new Date(userAction);
		};
		return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
	});

	const userDesignSystemIdSet = new Set(userDesignSystemIds);
	const sortedDesignSystems: DesignSystem[] = [...designSystems]
		.filter((ds: DesignSystem) => userDesignSystemIdSet.has(ds.id))
		.sort((a, b) => {
			const getMostRecentAction = (designSystem: DesignSystem) => {
				const creationDate = new Date(designSystem.createdAt);
				if (!designSystem.metadata?.openActions || !user?.id) return creationDate;
				const userAction = designSystem.metadata.openActions[user.id];
				if (!userAction) return creationDate;
				return new Date(userAction);
			};
			return getMostRecentAction(b).getTime() - getMostRecentAction(a).getTime();
		});

	const filter = filterText.toLowerCase();
	const filteredScenes = filter
		? sortedScenes.filter((s) => s.title.toLowerCase().includes(filter))
		: sortedScenes;
	const filteredGuestScenes = filter
		? sortedGuestScenes.filter((s) => s.title.toLowerCase().includes(filter))
		: sortedGuestScenes;
	const sortedGuestDesignSystems: DesignSystem[] = [...guestDesignSystems].sort((a, b) => {
		return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
	});

	const filteredDesignSystems = filter
		? sortedDesignSystems.filter((ds) => ds.title.toLowerCase().includes(filter))
		: sortedDesignSystems;
	const filteredGuestDesignSystems = filter
		? sortedGuestDesignSystems.filter((ds) => ds.title.toLowerCase().includes(filter))
		: sortedGuestDesignSystems;

	const displayedDesignSystems = showAllDesignSystems
		? filteredDesignSystems
		: filteredDesignSystems.slice(0, DESIGN_SYSTEMS_LIMIT);

	const displayedScenes = showAllProjects
		? filteredScenes
		: filteredScenes.slice(0, PROJECTS_LIMIT);

	const displayedGuestDesignSystems = showAllGuestDesignSystems
		? filteredGuestDesignSystems
		: filteredGuestDesignSystems.slice(0, DESIGN_SYSTEMS_LIMIT);

	const displayedGuestScenes = showAllGuestProjects
		? filteredGuestScenes
		: filteredGuestScenes.slice(0, PROJECTS_LIMIT);

	const remainingDesignSystems = filteredDesignSystems.length - displayedDesignSystems.length;
	const remainingGuestDesignSystems =
		filteredGuestDesignSystems.length - displayedGuestDesignSystems.length;
	const remainingProjects = filteredScenes.length - displayedScenes.length;
	const remainingGuestProjects = filteredGuestScenes.length - displayedGuestScenes.length;

	return (
		<>
			{isMobile && sidebarVisible && (
				<S.Overlay onClick={() => dispatch(setSidebarVisible(false))} />
			)}
			<S.Base
				isMobile={isMobile}
				sidebarVisible={sidebarVisible}
				sidebarDocked={sidebarDocked}
				sidebarWidth={sidebarWidth}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				{isMobile ? (
					<S.MobileHeader>
						<S.MobileHeaderContent>
							<S.LogoIconWrapper>
								<Icon type={IconType.MOONCHILD} width={24} height={24} color={theme.gray[1000]} />
							</S.LogoIconWrapper>
							<S.Moonchild>
								<Logo height={18} color={theme.gray[1000]} />
							</S.Moonchild>
						</S.MobileHeaderContent>
						<S.CloseButton onClick={() => dispatch(setSidebarVisible(false))}>
							<Icon type={IconType.CLOSE} width={20} height={20} color={theme.gray[1000]} />
						</S.CloseButton>
					</S.MobileHeader>
				) : (
					<S.DesktopHeader>
						<div />
						<S.DockButton onClick={() => dispatch(setSidebarDocked(!sidebarDocked))}>
							<Icon
								type={sidebarDocked ? IconType.SIDEBAR_UNFOLD : IconType.SIDEBAR_FOLD}
								width={22}
								height={22}
								color={theme.gray[800]}
							/>
						</S.DockButton>
					</S.DesktopHeader>
				)}
				<NewButton />
				{!isMobile && (
					<S.FilterWrapper>
						<S.FilterIcon>
							<Icon type={IconType.SEARCH} width={14} height={14} />
						</S.FilterIcon>
						<S.FilterInput
							placeholder="Filter..."
							value={filterText}
							onChange={(e) => setFilterText(e.target.value)}
							aria-label="Filter items"
						/>
						{filterText && (
							<S.FilterClearButton onClick={() => setFilterText('')} aria-label="Clear filter">
								<Icon type={IconType.CLOSE} width={14} height={14} />
							</S.FilterClearButton>
						)}
					</S.FilterWrapper>
				)}
				<S.Content>
					{loading ? (
						<S.LoadingWrapper>
							<Loader width={24} height={24} />
						</S.LoadingWrapper>
					) : (
						<>
							{!isMobile && filteredDesignSystems.length > 0 && (
								<>
									<S.SectionHeader>My Design Systems</S.SectionHeader>
									<S.ProjectsList>
										{displayedDesignSystems.map((designSystem: DesignSystem) => (
											<DesignSystemListItem
												key={designSystem.id}
												designSystem={designSystem}
												isActive={designSystem.id === activeDesignSystemId}
												onClick={() => onLoadDesignSystem(designSystem, false)}
												onCommandClick={() => onLoadDesignSystem(designSystem, true)}
											/>
										))}
										{remainingDesignSystems > 0 && (
											<S.ViewAllLink onClick={() => setShowAllDesignSystems(true)}>
												{remainingDesignSystems} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
							{!isMobile &&
								organizations.map((org) => {
									const orgDsIds = organizationDesignSystemIds[org.id] || [];
									const orgDesignSystems = orgDsIds
										.map((dsId) => designSystems.find((ds) => ds.id === dsId))
										.filter((ds): ds is DesignSystem => ds !== undefined)
										.filter((ds) => !filter || ds.title.toLowerCase().includes(filter));
									if (orgDesignSystems.length === 0) return null;
									return (
										<div key={`ds-${org.id}`}>
											<S.SectionHeader>{org.name} Design Systems</S.SectionHeader>
											<S.ProjectsList>
												{orgDesignSystems.map((designSystem: DesignSystem) => (
													<DesignSystemListItem
														key={designSystem.id}
														designSystem={designSystem}
														isActive={designSystem.id === activeDesignSystemId}
														onClick={() => onLoadDesignSystem(designSystem, false)}
														onCommandClick={() => onLoadDesignSystem(designSystem, true)}
													/>
												))}
											</S.ProjectsList>
										</div>
									);
								})}
							{!isMobile && filteredGuestDesignSystems.length > 0 && (
								<>
									<S.SectionHeader>Shared Design Systems</S.SectionHeader>
									<S.ProjectsList>
										{displayedGuestDesignSystems.map((designSystem: DesignSystem) => (
											<DesignSystemListItem
												key={designSystem.id}
												designSystem={designSystem}
												isActive={designSystem.id === activeDesignSystemId}
												onClick={() => onLoadDesignSystem(designSystem, false)}
												onCommandClick={() => onLoadDesignSystem(designSystem, true)}
											/>
										))}
										{remainingGuestDesignSystems > 0 && (
											<S.ViewAllLink onClick={() => setShowAllGuestDesignSystems(true)}>
												{remainingGuestDesignSystems} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
							{organizations.map((org) => {
								const orgSceneIds = organizationSceneIds[org.id] || [];
								const orgScenes = orgSceneIds
									.map((sceneId) => scenes.find((s) => s.id === sceneId))
									.filter((s): s is Scene => s !== undefined)
									.filter((s) => !filter || s.title.toLowerCase().includes(filter));
								if (orgScenes.length === 0) return null;
								return (
									<div key={org.id}>
										<S.SectionHeader>{org.name} Projects</S.SectionHeader>
										<S.ProjectsList>
											{orgScenes.map((scene: Scene) => (
												<ProjectListItem
													key={scene.id}
													scene={scene}
													isActive={scene.id === activeSceneId}
													onClick={() => onLoadScene(scene, false)}
													onCommandClick={() => onLoadScene(scene, true)}
												/>
											))}
										</S.ProjectsList>
									</div>
								);
							})}
							{filteredGuestScenes.length > 0 && (
								<>
									<S.SectionHeader>Shared Projects</S.SectionHeader>
									<S.ProjectsList>
										{displayedGuestScenes.map((scene: Scene) => (
											<ProjectListItem
												key={scene.id}
												scene={scene}
												isActive={scene.id === activeSceneId}
												onClick={() => onLoadScene(scene, false)}
												onCommandClick={() => onLoadScene(scene, true)}
											/>
										))}
										{remainingGuestProjects > 0 && (
											<S.ViewAllLink onClick={() => setShowAllGuestProjects(true)}>
												{remainingGuestProjects} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
							{filteredScenes.length > 0 && (
								<>
									<S.SectionHeader>My Projects</S.SectionHeader>
									<S.ProjectsList>
										{displayedScenes.map((scene: Scene) => (
											<ProjectListItem
												key={scene.id}
												scene={scene}
												isActive={scene.id === activeSceneId}
												onClick={() => onLoadScene(scene, false)}
												onCommandClick={() => onLoadScene(scene, true)}
											/>
										))}
										{remainingProjects > 0 && (
											<S.ViewAllLink onClick={() => setShowAllProjects(true)}>
												{remainingProjects} more
											</S.ViewAllLink>
										)}
									</S.ProjectsList>
								</>
							)}
						</>
					)}
				</S.Content>
				{!isGuest && (
					<S.Footer>
						{!isMobile && <AccountButton />}
						{isMobile && (
							<S.ProfileSettingsRow>
								<S.ProfileButton
									onClick={() => {
										navigate(routes.profile());
										if (isMobile) {
											dispatch(setSidebarVisible(false));
										}
									}}
								>
									<ProfileIcon size={32} />
									<S.ProfileName>{user?.displayName || ''}</S.ProfileName>
								</S.ProfileButton>
							</S.ProfileSettingsRow>
						)}
					</S.Footer>
				)}
			</S.Base>
		</>
	);
};

export default Sidebar;
